import { BaseModel } from "./base.model";
import { Unit } from "./unit.model";
import { Offer } from "./offer.model";
import { EmailTemplate } from "./email-template.model";
import { AssessmentRating } from "@helpers/assessment.helper";
import type { LocalizedText } from "@helpers/localized-text.helper";

export interface AssessmentComment {
  id: string;
  text: string;
  fieldKey: string;
  createdAt: string;
  createdBy: number;
  name: string;
  thumbUrl?: string;
}

export interface DocumentFile {
  id: number;
  originalName?: string;
  mimeType?: string;
}

export interface AssessmentFieldData {
  assessed: boolean;
  assessedAt?: string | null;
  assessedBy?: number | null;
  rating?: AssessmentRating;
}

export type AssessmentFieldsRecord = Record<string, AssessmentFieldData>;

export interface CrifCheckResult {
  success: boolean;
  payloadId: number;
  status: number;
  rating: 'A' | 'B' | 'C' | 'D' | null;
  decision?: 'GREEN' | 'YELLOW' | 'RED' | null;
  error?: string | null;
  errorCode?: string | null;
}

export interface ApplicationDocument {
  key: string;
  label: string;
  required: boolean;
  role?: string;
  files?: DocumentFile[][];
}

export interface ShortlistField {
  key: string;
  type: string;
  label: LocalizedText;
  hint?: LocalizedText | null;
  required: boolean;
  role?: string;
  conditions?: any;
  options?: any;
  choices?: { displayName: LocalizedText; value: string }[];
  /** Per-adult uploaded files — only set when type is 'fileUpload'. */
  files?: DocumentFile[][];
  /** Per-adult answer — set for every non-file-upload type. */
  values?: any[];
  /** Per-adult manual override of applicability — only meaningful (and only editable) on additional fields. */
  overrides?: (boolean | null)[];
  /** True only for ad-hoc fields an admin added directly in the request modal, not fields from the shortlist config. */
  additional?: boolean;
}


export class LeadFilters {
  unitIds: number[] = [];
  propertyIds: string[] = [];
  buildingIds: number[] = [];
  statuses: string[] = [];
  dateFrom: string | null = null;
  dateTo: string | null = null;
  contactSearch: string | null = null;
  isStarred = false;

  /** Statuses set at construction time are considered a preset, not a user filter. */
  private readonly presetStatuses: string[];

  constructor(data?: Partial<LeadFilters>) {
    if (data) Object.assign(this, data);
    this.presetStatuses = [...this.statuses];
  }

  getFilterQuery(): Record<string, any> {
    const query: Record<string, any> = {};

    if (this.unitIds.length) query['unit_ids[]'] = this.unitIds;
    if (this.propertyIds.length) query['property_ids[]'] = this.propertyIds;
    if (this.buildingIds.length) query['building_ids[]'] = this.buildingIds;
    if (this.statuses.length) query['statuses[]'] = this.statuses;
    if (this.dateFrom) query['date_from'] = this.dateFrom;
    if (this.dateTo) query['date_to'] = this.dateTo;
    if (this.contactSearch) query['contact_search'] = this.contactSearch;
    if (this.isStarred) query['starred'] = 1;

    return query;
  }

  get hasActiveFilters(): boolean {
    const nonPresetStatuses = this.statuses.filter(s => !this.presetStatuses.includes(s));
    return !!(this.unitIds.length || this.buildingIds.length || nonPresetStatuses.length || this.dateFrom || this.dateTo || this.contactSearch || this.isStarred);
  }

  reset(): void {
    this.unitIds = [];
    this.propertyIds = [];
    this.buildingIds = [];
    this.statuses = [];
    this.dateFrom = null;
    this.dateTo = null;
    this.contactSearch = null;
    this.isStarred = false;
  }
}

export class Lead extends BaseModel{
  static CONTACT_METHOD_EMAIL = 'email';
  static CONTACT_METHOD_PHONE = 'phone';

  static STATUS_SUBMITTED = 'submitted';
  static STATUS_IN_REVIEW = 'in-review';
  static STATUS_ASSESSED = 'assessed';
  static STATUS_OFFER_OPEN = 'offer-open';
  static STATUS_OFFER_REJECTED = 'offer-rejected';
  static STATUS_ASSIGNED = 'assigned';
  static STATUS_CONVERTED = 'converted';
  static STATUS_REJECTED = 'rejected';
  static STATUS_INACTIVE = 'inactive';

  static SHORTLIST_REQUEST_NOT_REQUESTED = 'not-requested';
  static SHORTLIST_REQUEST_REQUESTED = 'requested';
  static SHORTLIST_REQUEST_RECEIVED = 'received';

  static SALUTATION_MR = 'mr';
  static SALUTATION_MS = 'ms';

  static ROLE_EMPLOYER = 'employer';
  static ROLE_LANDLORD = 'landlord';

  id?: number;
  unitId?: number;
  instanceId?: number;
  hash?: string;
  salutation?: string;
  firstName!: string;
  lastName!: string;
  postalCode?: string;
  city?: string;
  contactMethod?: string;
  email?: string;
  phone?: string;
  status?: string;
  applicationData?: any;
  isStarred?: boolean;
  note?: string;
  assessmentData?: {
    summary?: { assessableCount: number; assessedCount: number };
    fields?: AssessmentFieldsRecord;
    comments?: AssessmentComment[];
  };

  initialAssessment?: 'A' | 'B' | 'C' | 'D';
  initialAssessmentClarification?: boolean;
  overallAssessment?: 'A' | 'B' | 'C' | 'D';
  overallAssessmentClarification?: boolean;
  documentData?: ApplicationDocument[];
  shortlistData?: ShortlistField[];
  emails?: Record<string, EmailTemplate>;

  createdBy?: number;
  updatedBy?: number;
  createdAt?: string;
  updatedAt?: string;

  unit?: Unit;
  otherUnits?: Unit[];
  offers?: Offer[];
  activeOffer?: Offer;
  activeOfferId?: number;

  get childModels() {
    return {
      unit: Unit,
      otherUnits: Unit,
      offers: Offer,
      activeOffer: Offer,
    };
  }

  get name(): string {
    const fullName = `${this.firstName ?? ''} ${this.lastName ?? ''}`.trim() || '-';
    const buildingName = this.unit?.building?.propertyId ?? '-';
    const unitName = this.unit?.name ?? '-';

    return `${unitName} – ${buildingName} (${fullName}) `;
  }

  get salutationLine(): { de: string; en: string; it: string } {
    const fullName = `${this.firstName ?? ''} ${this.lastName ?? ''}`.trim();

    if (this.salutation === Lead.SALUTATION_MS) {
      return {
        de: `Sehr geehrte Frau ${this.lastName ?? ''}`.trim(),
        en: `Dear Ms. ${this.lastName ?? ''}`.trim(),
        it: `Gentile signora ${this.lastName ?? ''}`.trim(),
      };
    }
    if (this.salutation === Lead.SALUTATION_MR) {
      return {
        de: `Sehr geehrter Herr ${this.lastName ?? ''}`.trim(),
        en: `Dear Mr. ${this.lastName ?? ''}`.trim(),
        it: `Gentile signor ${this.lastName ?? ''}`.trim(),
      };
    }
    return { de: `Guten Tag ${fullName}`, en: `Hello ${fullName}`, it: `Buongiorno ${fullName}` };
  }

  get buildingName(): string {
    const propertyId = this.unit?.building?.propertyId ?? '';
    const title = this.unit?.building?.title ?? '';

    const result = `${propertyId} - ${title}`.trim();

    return result || '-';
  }

  get unitName(): string {
    return this.unit?.name ?? '-';
  }

  get contact(): string {
    const phone = this.phone?.trim();
    const email = this.email?.trim();

    if (phone && email) {
      return `${phone} - ${email}`;
    }

    if (phone) {
      return phone;
    }

    if (email) {
      return email;
    }

    return '';
  }

  get location(): string {
    const postal = this.postalCode ?? '';
    const city = this.city ?? '';

    const result = `${postal} ${city}`.trim();

    return result || '-';
  }

  get applicationDataParsed(): Record<string, any> {
    if (!this.applicationData) {
      return {};
    }

    if (typeof this.applicationData === 'string') {
      try {
        return JSON.parse(this.applicationData);
      } catch {
        return {};
      }
    }

    return this.applicationData;
  }

  get applicationDataEntries(): { key: string; value: any }[] {
    const order = [
      'interestScope',
      'searchReason',
      'peopleCount',
      'language',
      'interestLevel',
      'subject',
      'message'
    ];

    return Object.entries(this.applicationDataParsed)
      .filter(([key, value]) =>
        key !== 'propertyIds' &&
        value !== null &&
        value !== ''
      )
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => {
        const indexA = order.indexOf(a.key);
        const indexB = order.indexOf(b.key);

        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });
  }

  get otherPropertyIds(): string[] {
    const ids: string[] = this.applicationDataParsed?.['propertyIds'] ?? [];
    const mainId = this.unit?.propertyId;

    return ids.filter((id: string) => id !== mainId);
  }

  get statusClass(): string {
    switch (this.status) {
      case Lead.STATUS_SUBMITTED:
        return 'badge-light-primary';
      case Lead.STATUS_IN_REVIEW:
        return 'badge-light-info';
      case Lead.STATUS_ASSESSED:
        return 'badge-light-warning';
      case Lead.STATUS_OFFER_OPEN:
        return 'badge-light-success';
      case Lead.STATUS_OFFER_REJECTED:
        return 'badge-light-danger';
      case Lead.STATUS_ASSIGNED:
        return 'badge-light-dark';
      case Lead.STATUS_CONVERTED:
        return 'badge-success';
      case Lead.STATUS_REJECTED:
        return 'badge-danger';
      case Lead.STATUS_INACTIVE:
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  }
}
